import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { TwoFactorChallenge } from '@/components/TwoFactorChallenge';

const Spinner = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const [aal, setAal] = useState<'checking' | 'ok' | 'needs2fa'>('checking');

  const checkAal = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      // Fail open on error so a transient API hiccup can't lock the user out.
      if (error || !data) return setAal('ok');
      setAal(data.nextLevel === 'aal2' && data.currentLevel !== 'aal2' ? 'needs2fa' : 'ok');
    } catch {
      setAal('ok');
    }
  }, []);

  useEffect(() => {
    if (!user) { setAal('checking'); return; }
    setAal('checking');
    checkAal();
  }, [user, checkAal]);

  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/auth" replace />;
  if (aal === 'checking') return <Spinner />;
  if (aal === 'needs2fa') return <TwoFactorChallenge onVerified={checkAal} />;
  return <>{children}</>;
}
